import React from 'react';
import type { Blueprint, Kitchen } from '@rta/rta-blueprint-library';

import type { BlueprintPage } from '../types';
import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { BlueprintPanelFooter } from '../shared/blueprint-panel-footer';
import { DisplayMode } from '@r-core';
import { EditableText } from '../shared/editable-text';
import { EmbeddedSvgViewport } from '../shared/embedded-svg-viewport';
import {
	FLAT_TRIM_BODY_STYLE,
	FLAT_TRIM_EMPTY_STYLE,
	FLAT_TRIM_FOOTER_STYLE,
	FLAT_TRIM_NOTE_LABEL_STYLE,
	FLAT_TRIM_NOTES_STYLE,
	FLAT_TRIM_VIEWPORT_STYLE
} from './blueprint-flat-trim-page.styles';
import { getBaseMaterialLabel } from '../helpers/get-base-material-label';
import { getCornerTrimNoteLines } from '../helpers/get-corner-trim-note-lines';
import { getPageNoteScope } from '../helpers/get-page-note-scope';
import { getPageOverrideScope } from '../helpers/get-page-override-scope';
import { needsFlatTrimPage } from '../helpers/needs-flat-trim-page';

export type BlueprintFlatTrimPageProps = {
	page: BlueprintPage;
	blueprint: Blueprint;
	kitchen: Kitchen;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

export const BlueprintFlatTrimPage = (props: BlueprintFlatTrimPageProps): React.ReactElement => {
	const {
		page,
		blueprint,
		kitchen,
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl
	} = props;

	// the page embeds the real corner-trim viewport, so the pieces always match the designer's flat trim view
	const firstIslandId = kitchen.design.islands.children[0]?.id ?? '';
	const hasFlatTrims = needsFlatTrimPage({ kitchen });
	const noteLines = [
		{ label: 'Base:', value: getBaseMaterialLabel(kitchen.material?.surround?.displayName) },
		...getCornerTrimNoteLines(kitchen)
	];
	const scope = getPageOverrideScope(page);

	return (
		<BlueprintPageWrapper pageNumber={page.pageNumber}>
			<BlueprintPageHeader
				title={page.title}
				pageNumber={page.pageNumber}
				customerName={customerName}
				layoutDescription={layoutDescription}
				designerName={designerName}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				showThumbnail={true}
			/>

			<div style={FLAT_TRIM_BODY_STYLE}>
				<EditableText overrideKey={`flatTrim.${scope}.notes`} style={FLAT_TRIM_NOTES_STYLE}>
					{noteLines.map(({ label, value }, index) => (
						<React.Fragment key={index}>
							{index > 0 && <br />}
							<span style={FLAT_TRIM_NOTE_LABEL_STYLE}>{label} </span>
							{value}
						</React.Fragment>
					))}
				</EditableText>

				<div style={FLAT_TRIM_VIEWPORT_STYLE}>
					{hasFlatTrims ? (
						<EmbeddedSvgViewport
							blueprint={blueprint}
							islandId={firstIslandId}
							panelId={null}
							displayMode={DisplayMode.CornerTrimManufacturing}
							pageIndex={page.pageNumber}
							pageType={page.pageType}
						/>
					) : (
						<EditableText overrideKey='flatTrim.empty' style={FLAT_TRIM_EMPTY_STYLE}>
							FLAT TRIM PIECE
						</EditableText>
					)}
				</div>
			</div>

			{/* Combined panel footer + page footer */}
			<div style={FLAT_TRIM_FOOTER_STYLE}>
				<BlueprintPanelFooter scope={scope} notes={[]} noteScope={getPageNoteScope(page)} />
				<BlueprintPageFooter pageNumber={page.pageNumber} />
			</div>
		</BlueprintPageWrapper>
	);
};
